module Hereabout.UI.Components.CommentComponent where

import Prelude

import Control.Monad.Rec.Class (forever)
import Data.DateTime (DateTime(..), Month(..), diff, exactDate)
import Data.Enum (toEnum)
import Data.Formatter.DateTime as DT
import Data.Int (ceil)
import Data.List as L
import Data.Maybe (Maybe(..), fromMaybe)
import Data.Maybe as M
import Data.Time.Duration as DUR
import Effect.Aff (Milliseconds(..))
import Effect.Aff as Aff
import Effect.Aff.Class (class MonadAff)
import Effect.Class (class MonadEffect, liftEffect)
import Effect.Now (nowDateTime)
import Halogen as H
import Halogen.HTML as HH
import Halogen.HTML.Events as HE
import Halogen.HTML.Properties as HP
import Halogen.HTML.Properties.ARIA as PA
import Halogen.Subscription as HS
import Hereabout.Data.Route (Route(..))
import Hereabout.Effects (class Navigate, navigate)
import Hereabout.Types (Event(..), Comment(..), Place(..), User(..), Venue(..))
import Hereabout.UI.Bits (renderIcon)
import Hereabout.UI.Bulma.Classes as Bulma
import Hereabout.UI.Components.RawHTMLComponent as RawHTMLComp
import Hereabout.UI.SemanticUI.Classes as SemUI
import Type.Proxy (Proxy(..))



data Action
  = OpenReportFlow
  | CloseReportFlow
  | InitComponent
  | Tick

type State =
  { message :: Comment String
  , isReportFlowOpen :: Boolean
  , lastUpdated :: Maybe DateTime
  }

type Slot id = forall q. H.Slot q Void id

type ChildSlots =
  ( htmlMessageComp :: RawHTMLComp.Slot Unit
  )

component :: forall q o m. MonadAff m => Navigate m => H.Component q (Comment String) o m
component =
  H.mkComponent
    { initialState: \msg -> { message : msg, isReportFlowOpen : false, lastUpdated : Nothing }
    , render
    , eval: H.mkEval H.defaultEval { handleAction = handleAction, initialize = Just InitComponent }
    }

formatTimeDiff :: DateTime -> DateTime -> String
formatTimeDiff new old = 
  let (DUR.Minutes mins) = diff new old in
  case ceil mins of
    1 -> show 1 <> " min"
    x -> show x <> " mins"


render :: forall m. MonadEffect m => State -> H.ComponentHTML Action ChildSlots m
render state =
  HH.div_ $ 
    [ HH.div [ HP.classes [ Bulma.box, Bulma.mb1 ] ]
      [ renderMessage state.lastUpdated false state.message
      ]
    ] <> reportFlow
  where 
    reportFlow =
      if state.isReportFlowOpen then
        [ HH.div [ HP.classes [ Bulma.modal, Bulma.isActive ] ]
          [ HH.div [ HP.classes [ Bulma.modalBackground] ]
            [
            ]
          , HH.div [ HP.classes [ Bulma.modalCard ] ]
            [ HH.header [ HP.classes [ Bulma.modalCardHead] ]
              [ HH.p [ HP.classes [ Bulma.modalCardTitle ] ]
                [ HH.text "Report Comment"
                ]
              , HH.button [ HP.classes [ Bulma.delete ], HE.onClick $ \_ -> CloseReportFlow ] []
              ]
            , HH.section [ HP.classes [ Bulma.modalCardBody ] ]
              [ HH.article [ HP.classes [ Bulma.message, Bulma.isDanger ] ]
                [ HH.div [ HP.classes [ Bulma.messageHeader ] ]
                  [ HH.text "Comment to report"
                  ]
                , HH.div [ HP.classes [ Bulma.messageBody] ]
                  [ renderMessage state.lastUpdated false state.message
                  ]
                ]
              , HH.div [ HP.classes [ Bulma.block ] ]
                [ HH.div [HP.classes [Bulma.content ]]
                  [ HH.h3_ [ HH.text "Why are you reporting this message?" ]
                  , HH.button [ HP.classes [ Bulma.button ] ] [ HH.text "False, misleading, defamatory, or offensive content." ]
                  , HH.button [ HP.classes [ Bulma.button ] ] [ HH.text "Harassment, discrimination, or hate speech." ]
                  , HH.button [ HP.classes [ Bulma.button ] ] [ HH.text "Spam, phishing, etc." ]
                  ]

                ]
              ]
            ]
          ]
        ]
      else
        [
        ]
    
renderMessage :: forall m. MonadEffect m => Maybe DateTime -> Boolean -> Comment String -> H.ComponentHTML Action ChildSlots m
renderMessage lastUpdated renderControls (Comment msg) =
  HH.article [ HP.classes [ Bulma.media ] ]
    [ HH.div [ HP.classes [ Bulma.mediaLeft ] ]
      [ HH.figure [ HP.classes [ Bulma.image, Bulma.is64x64 ] ]
        [ HH.img [ HP.src user.imageUrl  ]
        ]
      ]
    , HH.div [ HP.classes [ Bulma.mediaContent ] ] $
      [ HH.div [ HP.classes [ Bulma.content ] ]
        [ HH.p_ 
          [ HH.strong [ HP.classes [ Bulma.mr3 ] ] [ HH.text user.fullName ]
          , HH.small_ [  HH.text $ fromMaybe "" $ map (\x -> formatTimeDiff x msg.timestamp) lastUpdated ]
          ]
        , HH.p_ 
          [ HH.slot_ (Proxy :: _ "htmlMessageComp") unit RawHTMLComp.component $ { html : msg.content }
          ]
        ]
      ] <> controlsOrEmpty
    ]
    where 
      (User user) = msg.user
      controlsOrEmpty =
        if renderControls then
          [ HH.nav [ HP.classes [ Bulma.level, Bulma.isMobile ] ]
            [ HH.div [ HP.classes [ Bulma.levelLeft ] ]
              [ HH.a [ HP.classes [ Bulma.levelItem ], PA.label "Report", HE.onClick $ \_ -> OpenReportFlow ] $
                [ renderIcon "fa-flag" $ HH.text "Report"
                ]
              ]
            ]
          ]
        else 
          []

handleAction :: forall cs o m. MonadAff m => Navigate m => Action -> H.HalogenM State Action cs o m Unit
handleAction InitComponent = do
    _ <- H.subscribe =<< tickEmitter
    pure unit
handleAction OpenReportFlow =
    H.modify_ _ { isReportFlowOpen = true }
handleAction CloseReportFlow =
    H.modify_ _ { isReportFlowOpen = false }
handleAction Tick = do
  now <- liftEffect nowDateTime
  H.modify_ _ {lastUpdated = Just now}
  pure unit

    

tickEmitter ∷ forall m. Bind m ⇒ MonadAff m ⇒ m (HS.Emitter Action)
tickEmitter = do
  { emitter, listener } <- H.liftEffect HS.create
  _ <- H.liftAff $ Aff.forkAff $ forever do
    Aff.delay $ Milliseconds 1000.0
    H.liftEffect $ HS.notify listener $ Tick
  pure emitter

